import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Box,
  Tabs,
  Tab,
  Container,
  Paper,
  Snackbar,
  Alert,
  Button,
  Menu,
  MenuItem,
  Avatar,
} from '@mui/material';
import {
  Payments as PredictionsIcon,
  Assessment as AssessmentIcon,
  Analytics as AnalyticsIcon,
  Info as InfoIcon,
  ModelTraining as ModelManagementIcon,
  Build as TrainingIcon,
  Storage as DataIcon,
  Autorenew as RefinanceIcon,
  Science as ExpertIcon,
  AccountCircle,
  Logout,
} from '@mui/icons-material';

import { useAuth } from './auth/SimpleKeycloakProvider';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { LoginPage } from './components/LoginPage';
import { TabPanelProps } from './types';
import LoanDefaultTab from './components/LoanDefaultTab';
import ScorecardTab from './components/ScorecardTab';
import UnifiedCreditAnalysis from './components/UnifiedCreditAnalysis';
import RefinanceTab from './components/RefinanceTab';
import AnalysisTab from './components/AnalysisTab';
import ModelInfoTab from './components/ModelInfoTab';
import ModelManagement from './components/ModelManagement';
import TrainingManagement from './components/TrainingManagement';
import DatasetManagement from './components/DatasetManagement';
import ExpertTab from './components/ExpertTab';

// Create Material-UI theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    h4: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 500,
    },
  },
});

// TabPanel component for tab content
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`credit-risk-tabpanel-${index}`}
      aria-labelledby={`credit-risk-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Tab accessibility props
function a11yProps(index: number) {
  return {
    id: `credit-risk-tab-${index}`,
    'aria-controls': `credit-risk-tabpanel-${index}`,
  };
}

function MainApp() {
  const { authenticated, user, logout, hasRole, hasAnyRole } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCloseNotification = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification({ ...notification, open: false });
  };

  const showNotification = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    logout();
  };

  // Role-based tab visibility
  const canViewAnalysis = hasAnyRole(['viewer', 'analyst', 'risk_manager', 'admin']);
  const canManageModels = hasAnyRole(['risk_manager', 'admin']);
  const canTrainModels = hasAnyRole(['admin']);

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            SUMA Credit Risk ML Platform
          </Typography>

          {authenticated && (
            <div>
              <Button
                color="inherit"
                onClick={handleMenu}
                startIcon={<AccountCircle />}
              >
                {user?.firstName || user?.username || 'User'}
              </Button>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem disabled>
                  <Typography variant="body2" color="textSecondary">
                    {user?.email}
                  </Typography>
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <Logout fontSize="small" sx={{ mr: 1 }} />
                  Logout
                </MenuItem>
              </Menu>
            </div>
          )}
        </Toolbar>
      </AppBar>

      {/* Navigation Tabs */}
      <Paper elevation={1} sx={{ borderRadius: 0 }}>
        <Container maxWidth="xl">
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="credit risk platform tabs"
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            {canViewAnalysis && (
              <Tab
                icon={<AssessmentIcon />}
                iconPosition="start"
                label="Credit Analysis"
                {...a11yProps(0)}
                sx={{ minHeight: 64, textTransform: 'none' }}
              />
            )}
            {canViewAnalysis && (
              <Tab
                icon={<AnalyticsIcon />}
                iconPosition="start"
                label="Data Analysis"
                {...a11yProps(1)}
                sx={{ minHeight: 64, textTransform: 'none' }}
              />
            )}
            {canViewAnalysis && (
              <Tab
                icon={<InfoIcon />}
                iconPosition="start"
                label="Model Performance"
                {...a11yProps(2)}
                sx={{ minHeight: 64, textTransform: 'none' }}
              />
            )}
            {canManageModels && (
              <Tab
                icon={<ModelManagementIcon />}
                iconPosition="start"
                label="Gestión de Modelos"
                {...a11yProps(3)}
                sx={{ minHeight: 64, textTransform: 'none' }}
              />
            )}
            {canTrainModels && (
              <Tab
                icon={<TrainingIcon />}
                iconPosition="start"
                label="Entrenamiento"
                {...a11yProps(4)}
                sx={{ minHeight: 64, textTransform: 'none' }}
              />
            )}
            {canManageModels && (
              <Tab
                icon={<DataIcon />}
                iconPosition="start"
                label="Gestión de Datos"
                {...a11yProps(5)}
                sx={{ minHeight: 64, textTransform: 'none' }}
              />
            )}
            {canViewAnalysis && (
              <Tab
                icon={<RefinanceIcon />}
                iconPosition="start"
                label="Refinanciamiento"
                {...a11yProps(6)}
                sx={{ minHeight: 64, textTransform: 'none' }}
              />
            )}
            {canViewAnalysis && (
              <Tab
                icon={<ExpertIcon />}
                iconPosition="start"
                label="Experto"
                {...a11yProps(7)}
                sx={{ minHeight: 64, textTransform: 'none' }}
              />
            )}
          </Tabs>
        </Container>
      </Paper>

      {/* Tab Content */}
      <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
        {canViewAnalysis && (
          <TabPanel value={tabValue} index={0}>
            <UnifiedCreditAnalysis onNotification={showNotification} />
          </TabPanel>
        )}
        {canViewAnalysis && (
          <TabPanel value={tabValue} index={1}>
            <AnalysisTab onNotification={showNotification} />
          </TabPanel>
        )}
        {canViewAnalysis && (
          <TabPanel value={tabValue} index={2}>
            <ModelInfoTab onNotification={showNotification} />
          </TabPanel>
        )}
        {canManageModels && (
          <TabPanel value={tabValue} index={3}>
            <ModelManagement onNotification={showNotification} />
          </TabPanel>
        )}
        {canTrainModels && (
          <TabPanel value={tabValue} index={4}>
            <TrainingManagement onNotification={showNotification} />
          </TabPanel>
        )}
        {canManageModels && (
          <TabPanel value={tabValue} index={5}>
            <DatasetManagement onNotification={showNotification} />
          </TabPanel>
        )}
        {canViewAnalysis && (
          <TabPanel value={tabValue} index={6}>
            <RefinanceTab onNotification={showNotification} />
          </TabPanel>
        )}
        {canViewAnalysis && (
          <TabPanel value={tabValue} index={7}>
            <ExpertTab onNotification={showNotification} />
          </TabPanel>
        )}
      </Container>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function AppWithAuth() {
  const { authenticated } = useAuth();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/"
            element={
              <ProtectedRoute roles={['viewer', 'analyst', 'risk_manager', 'admin']}>
                <MainApp />
              </ProtectedRoute>
            }
          />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default AppWithAuth;